/** @jsxImportSource theme-ui */
import { Spinner } from 'theme-ui';
import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import {
    useGetIncentiveActionsQuery,
    useLazyGetLicenseTypesQuery,
    usePostIncentiveMutation,
} from '@/store/apiService';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import { PostIncentive } from '@/types/types';
import { toast } from 'react-toastify';
import IncentiveForm, { validateIncentiveDates } from './IncentiveForm';
import PreviewIncentive from './PreviewIncentive';
import Error from '@/components/Error';
import RTKLoader from '@/components/RTKLoader';

// TODO: add Loader component that takes queries and displays errors or spinner based on the response

type Props = {};

const CreateIncentive: React.FC<Props> = (props: Props) => {
    const { id } = useParams<{ id: string }>();
    const [createIncentive, createIncentiveState] = usePostIncentiveMutation();
    const [getLicenseTypes, licenseTypes] = useLazyGetLicenseTypesQuery({});
    const actions = useGetIncentiveActionsQuery({});
    const [actionId, setActionId] = useState(0);
    const dialog = useDialogState();

    const licenseTypesOptions = licenseTypes.data
        ? licenseTypes.data.Results.map((licenceType) => ({
            // @ts-ignore
            value: licenceType.Id,
            label: licenceType.Name,
        }))
        : [];
    let navigate = useNavigate();

    const initialValues: PostIncentive & { _content: 'file' | 'storage' } = {
        Active: true,
        Heading: '',
        StorageUrl: '',
        Frequency: 'OneTime',
        InitialDelayInSeconds: 0,
        // @ts-ignore add after types are genarated
        InitialDeferredDays: 0,
        MaxDisplayCount: 0,
        SuccessButtonText: '',
        // Use null when not set so we can send null to the API
        // @ts-ignore backend accepts null for missing dates
        ValidFrom: null,
        // @ts-ignore backend accepts null for missing dates
        ValidTo: null,
        Body: '',
        RecurrenceInterval: 0,
        CompanyIds: id ? [id] : [],
        Criteria: [],
        ActionId: 1,
        _content: 'file',
    };

    useEffect(() => {
        getLicenseTypes({});
    }, [actionId]);

    return (
        <>
            <RTKLoader queries={[actions]}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validate={validateIncentiveDates}
                    onSubmit={(values, { resetForm, setErrors, setSubmitting }) => {
                        const normalizeDateForApi = (value: string | null | undefined) => {
                            return value ? value : null;
                        };

                        const sanitizedFormValues: any = {
                            ...values,
                            // @ts-ignore
                            Criteria: values.Criteria.filter((c) => c.checked),
                        };

                        sanitizedFormValues.ValidFrom = normalizeDateForApi(values.ValidFrom as any);
                        sanitizedFormValues.ValidTo = normalizeDateForApi(values.ValidTo as any);

                        createIncentive(sanitizedFormValues)
                            .unwrap()
                            .then((res) => {
                                if (id) {
                                    navigate(`/clients/${id}`);
                                } else {
                                    navigate(`/incentives`);
                                }
                            })
                            .catch((err) => {
                                setSubmitting(false);
                                toast.error(err.data, { autoClose: 5000 });
                            });
                    }}
                >
                    {(formProps) => {
                        return (
                            <>
                                <IncentiveForm
                                    {...formProps}
                                    actionId={actionId}
                                    setActionId={setActionId}
                                    licenseTypesOptions={licenseTypesOptions}
                                    additionalErrors={createIncentiveState.error}
                                    actions={actions.data.Results}
                                    addToFooter={
                                        <Button
                                            variant="secondary"
                                            sx={{
                                                marginLeft: 'auto',
                                            }}
                                            onClick={() => dialog.show()}
                                        >
                                            Preview incentive
                                        </Button>
                                    }
                                />

                                <DialogBackdrop {...dialog}>
                                    <Dialog
                                        sx={{
                                            width: '2/3',
                                            maxWidth: '640px',
                                            '&&': {
                                                padding: 4,
                                                minHeight: '480px',
                                                overflow: 'scroll',
                                            },
                                        }}
                                        {...dialog}
                                    >
                                        <PreviewIncentive {...formProps.values} />
                                    </Dialog>
                                </DialogBackdrop>
                            </>
                        );
                    }}
                </Formik>
            </RTKLoader>
        </>
    );
};

export default CreateIncentive;
